/**
 * Photo marker transform utilities.
 *
 * Provides functions for handling photo marker transforms,
 * particularly keeping text horizontal during rotation.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle } from "../core/oda";
import { OpenMode } from "../core/constants";
import { isPhotoMarkerEntity } from "./detection";
import { applyScales2D } from "../blocks/transform";
import type {
  AttrScaleSnapshot,
  SetPhotoMarkerScaleParams,
} from "../commands/SetPhotoMarkerScaleCommand";
import type { SetPhotoMarkerAttributeParams } from "../commands/SetPhotoMarkerAttributeCommand";
import { recordGraphicsModified } from "../entities";

/**
 * Counter-rotate all attributes (text) on a photo marker block reference
 * to keep them horizontal after the block has been rotated.
 *
 * This is called after rotating a photo marker to ensure the text
 * remains readable (horizontal) regardless of marker rotation.
 *
 * @param module The ODA drawing module
 * @param entityId The entity ID (must be a photo marker block reference)
 * @param rotationDelta The rotation angle that was applied (in radians)
 */
export function counterRotatePhotoMarkerText(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  rotationDelta: number,
): void {
  if (Math.abs(rotationDelta) < 0.0001) return;

  // Check if it's a photo marker
  if (!isPhotoMarkerEntity(module, entityId)) return;

  // Open the block reference for write to access attributes
  const pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
  const blockRef = module.OdDbBlockReference.cast(pObj);
  deleteObj(pObj);
  if (!blockRef) return;

  // Iterate through attributes and counter-rotate each one
  const attrIter = blockRef.attributeIterator();
  while (!attrIter.done()) {
    const attrId = attrIter.objectId();

    // Open attribute for write
    const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForWrite);

    // Get current rotation and subtract the delta to keep horizontal
    const currentRotation = attr.rotation();
    const newRotation = currentRotation - rotationDelta;
    attr.setRotation(newRotation);
    deleteObj(attr);
    deleteObj(attrId);

    attrIter.step(true, true);
  }

  deleteObj(attrIter);
  deleteObj(blockRef);
}

/**
 * Reset all attribute rotations on a photo marker to zero (horizontal).
 *
 * @param module The ODA drawing module
 * @param entityId The entity ID (must be a photo marker block reference)
 */
export function resetPhotoMarkerTextRotation(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): void {
  // Check if it's a photo marker
  if (!isPhotoMarkerEntity(module, entityId)) return;

  // Open the block reference for write to access attributes
  const pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
  const blockRef = module.OdDbBlockReference.cast(pObj);
  deleteObj(pObj);
  if (!blockRef) return;

  // Iterate through attributes and reset rotation to 0
  const attrIter = blockRef.attributeIterator();
  while (!attrIter.done()) {
    const attrId = attrIter.objectId();

    const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForWrite);
    attr.setRotation(0);
    deleteObj(attr);
    deleteObj(attrId);

    attrIter.step(true, true);
  }

  deleteObj(attrIter);
  deleteObj(blockRef);
}

/**
 * Read the text height of the first attribute on a photo marker block reference.
 * Returns null if the entity is not a block-mode photo marker.
 */
export function getPhotoMarkerTextHeight(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): number | null {
  if (!isPhotoMarkerEntity(module, entityId)) return null;

  const pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
  const blockRef = module.OdDbBlockReference.cast(pObj);
  deleteObj(pObj);
  if (!blockRef) return null;

  const attrIter = blockRef.attributeIterator();
  let height: number | null = null;
  if (!attrIter.done()) {
    const attrId = attrIter.objectId();
    if (!attrId.isNull()) {
      const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForRead);
      height = attr.height();
      deleteObj(attr);
    }
    deleteObj(attrId);
  }
  deleteObj(attrIter);
  deleteObj(blockRef);
  return height;
}

/** Texte porté par l'entité elle-même (marqueur en mode entité). */
function readOwnTextValue(
  module: DrawingModuleType,
  obj: DrawingModule.OdDbObject,
): string | null {
  const asText = module.OdDbText.cast(obj);
  if (asText) {
    const s = asText.textString();
    const value = s.toString();
    deleteObj(s);
    deleteObj(asText);
    return value;
  }

  const asMText = module.OdDbMText.cast(obj);
  if (asMText) {
    const s = asMText.contents();
    // Codes de formatage MText retirés : « \H2x;12 » doit se lire « 12 ».
    const value = s
      .toString()
      .replace(/\\P/g, " ")
      .replace(/\\[A-Za-z][^;\\]*;/g, "")
      .replace(/[{}]/g, "")
      .trim();
    deleteObj(s);
    deleteObj(asMText);
    return value;
  }

  return null;
}

/**
 * Read the label carried by a photo marker.
 *
 * Block-mode markers hold it in their first attribute. Entity-mode markers (and
 * notably the "text only" style, which has no geometry at all) are a plain
 * Text/MText tagged with the photo marker xData: the label is then the entity's
 * own text. Without that fallback such markers came back unlabelled.
 */
export function getPhotoMarkerAttributeValue(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): string | null {
  if (!isPhotoMarkerEntity(module, entityId)) return null;

  const pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
  const blockRef = module.OdDbBlockReference.cast(pObj);
  if (!blockRef) {
    const own = readOwnTextValue(module, pObj);
    deleteObj(pObj);
    return own;
  }
  deleteObj(pObj);

  const attrIter = blockRef.attributeIterator();
  let value: string | null = null;
  if (!attrIter.done()) {
    const attrId = attrIter.objectId();
    const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForRead);
    const odValue = attr.textString();
    value = odValue.toString();
    deleteObj(odValue);
    deleteObj(attr);
    deleteObj(attrId);
  }
  deleteObj(attrIter);
  deleteObj(blockRef);
  return value;
}

/**
 * Set the text value of the first attribute on a photo marker block reference.
 * Returns params for the undo/redo command, or null if not a block-mode photo marker.
 */
export function setPhotoMarkerAttributeValue(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  newValue: string,
): SetPhotoMarkerAttributeParams | null {
  if (!isPhotoMarkerEntity(module, entityId)) return null;

  const pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
  const blockRef = module.OdDbBlockReference.cast(pObj);
  deleteObj(pObj);
  if (!blockRef) return null;

  const blockHandle = getEntityHandle(entityId);

  const attrIter = blockRef.attributeIterator();
  let result: SetPhotoMarkerAttributeParams | null = null;
  if (!attrIter.done()) {
    const attrId = attrIter.objectId();
    const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForWrite);
    const odOldValue = attr.textString();
    const oldValue = odOldValue.toString();
    deleteObj(odOldValue);
    const odNewValue = new module.OdString(newValue);
    attr.setTextString(odNewValue);
    deleteObj(odNewValue);
    result = {
      blockHandle,
      attrHandle: getEntityHandle(attrId),
      oldValue,
      newValue,
    };
    deleteObj(attr);
    deleteObj(attrId);
  }
  deleteObj(attrIter);
  deleteObj(blockRef);
  return result;
}

/**
 * Rescale a photo marker block reference to a new attribute text height.
 *
 * Scales the block reference geometry proportionally and updates each
 * attribute's height and alignment point to match.
 *
 * @returns Params for the undo/redo command, or null if not a block-mode photo marker
 */
export function rescalePhotoMarker(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  newHeight: number,
): SetPhotoMarkerScaleParams | null {
  if (!isPhotoMarkerEntity(module, entityId)) return null;

  const pObj = entityId.safeOpenObject(OpenMode.kForWrite, false);
  const blockRef = module.OdDbBlockReference.cast(pObj);
  deleteObj(pObj);
  if (!blockRef) return null;

  const blockHandle = getEntityHandle(entityId);
  const currentScaleObj = blockRef.scaleFactors();
  const oldBlockScale = currentScaleObj.sx;
  deleteObj(currentScaleObj);

  const insertPt = blockRef.position();
  const oldAttrs: AttrScaleSnapshot[] = [];
  const newAttrs: AttrScaleSnapshot[] = [];

  // Derive the scale ratio from the first attribute's current height
  let ratio: number | null = null;
  let newBlockScale = oldBlockScale;

  const attrIter = blockRef.attributeIterator();
  while (!attrIter.done()) {
    const attrId = attrIter.objectId();
    const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForWrite);

    const oldHeight = attr.height();
    const oldAlignPt = attr.alignmentPoint();

    oldAttrs.push({
      handle: getEntityHandle(attrId),
      height: oldHeight,
      alignmentX: oldAlignPt.x,
      alignmentY: oldAlignPt.y,
    });

    if (oldHeight > 0) {
      if (ratio === null) {
        ratio = newHeight / oldHeight;
        newBlockScale = oldBlockScale * ratio;
        applyScales2D(module, blockRef, newBlockScale, newBlockScale, 1);
        recordGraphicsModified(module, entityId);
      }

      const scaledHeight = oldHeight * ratio;
      const newAlignX = insertPt.x + (oldAlignPt.x - insertPt.x) * ratio;
      const newAlignY = insertPt.y + (oldAlignPt.y - insertPt.y) * ratio;

      attr.setHeight(scaledHeight);
      const newPt = new module.OdGePoint3d(newAlignX, newAlignY, 0);
      attr.setAlignmentPoint(newPt);
      deleteObj(newPt);

      recordGraphicsModified(module, attrId);

      newAttrs.push({
        handle: getEntityHandle(attrId),
        height: scaledHeight,
        alignmentX: newAlignX,
        alignmentY: newAlignY,
      });
    }

    deleteObj(oldAlignPt);
    deleteObj(attr);
    deleteObj(attrId);
    attrIter.step(true, true);
  }

  deleteObj(attrIter);
  deleteObj(insertPt);
  deleteObj(blockRef);

  if (ratio === null) return null;

  return { blockHandle, oldBlockScale, newBlockScale, oldAttrs, newAttrs };
}
